import { FC } from 'react';
import styles from './PrivacyPage.module.scss';

/**
 * Pagina placeholder Privacy Policy. Documento legale ufficiale
 * sostituirà questo testo. Riferimento art. 13/14 GDPR
 * (informativa).
 */
export const PrivacyPage: FC = () => {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1>Informativa sulla privacy</h1>
        <p className={styles.intro}>
          <em>
            Versione 0.9 (pre-legal review). Per richieste di
            accesso, portabilità o cancellazione dati: contattare{' '}
            <a href="mailto:privacy@leopartmove.com">
              privacy@leopartmove.com
            </a>
            . La presente informativa è redatta ai sensi degli
            artt. 13 e 14 del Regolamento (UE) 2016/679 (GDPR) e
            del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018.
          </em>
        </p>

        <h2>Titolare del trattamento</h2>
        <p>
          LeoPart S.r.l. — sede legale e contatti DPO disponibili a
          richiesta tramite email a{' '}
          <a href="mailto:privacy@leopartmove.com">
            privacy@leopartmove.com
          </a>
          .
        </p>

        <h2>Dati raccolti</h2>
        <ul>
          <li>
            <strong>Identificativi</strong>: nome, cognome, email,
            data di nascita, foto profilo, telefono.
          </li>
          <li>
            <strong>Dati salute (categoria speciale art. 9 GDPR)</strong>:
            questionari Hooper (sonno, fatica, stress, umore, dolori
            muscolari), metriche fisiologiche (FCR, HRV), recovery
            index, BodyMap, protocolli return-to-play, complaints
            muscolari, anamnesi.
          </li>
          <li>
            <strong>Dati comportamentali</strong>: sessioni di
            allenamento, log esecuzioni, feedback, sessioni GPS,
            test fisici, profilazione VBT/Sprint/Yo-Yo.
          </li>
          <li>
            <strong>Dati tecnici</strong>: token di autenticazione,
            token push device, log errori applicativi.
          </li>
        </ul>

        <h2>Finalità del trattamento</h2>
        <ul>
          <li>
            Erogazione del servizio di monitoraggio sportivo per
            atleti e staff tecnico (esecuzione contratto, art. 6(1)(b)).
          </li>
          <li>
            Calcolo recovery, readiness, antifragility, statistiche
            di performance team (esecuzione contratto +
            consenso esplicito per dati salute, art. 9(2)(a)).
          </li>
          <li>
            Notifiche operative (push, email) — opt-out disponibile.
          </li>
        </ul>

        <h2>Conservazione</h2>
        <p>
          I dati sono conservati per la durata del rapporto contrattuale
          e cancellati o anonimizzati entro 18 mesi dalla cessazione
          dell&apos;account. Audit log conservati 5 anni
          (accountability art. 5 GDPR).
        </p>

        <h2>Diritti dell&apos;interessato (art. 15-22 GDPR)</h2>
        <ul>
          <li>
            <strong>Accesso (art. 15)</strong>: richiedere copia dei
            dati personali — endpoint{' '}
            <code>GET /users/:id/gdpr/export</code>.
          </li>
          <li>
            <strong>Rettifica (art. 16)</strong>: aggiornare i propri
            dati dalle impostazioni del profilo.
          </li>
          <li>
            <strong>Cancellazione (art. 17)</strong>: richiedere
            l&apos;eliminazione — endpoint{' '}
            <code>DELETE /users/:id/gdpr/erasure</code> oppure email
            a privacy@leopartmove.com.
          </li>
          <li>
            <strong>Portabilità (art. 20)</strong>: ricevere i dati
            in formato JSON strutturato (stesso endpoint export).
          </li>
          <li>
            <strong>Opposizione e limitazione (art. 18-21)</strong>:
            via email DPO.
          </li>
          <li>
            <strong>Reclamo</strong>: presso Garante per la protezione
            dei dati personali (
            <a href="https://www.garanteprivacy.it">garanteprivacy.it</a>
            ).
          </li>
        </ul>

        <h2>Sub-processor</h2>
        <p>
          LeoPart si avvale dei seguenti sub-processor (DPA in essere
          al momento della redazione legale definitiva):
        </p>
        <ul>
          <li>MongoDB Atlas (hosting database, EU region)</li>
          <li>Amazon Web Services (hosting backend + storage S3, eu-central-1)</li>
          <li>AWS Amplify (frontend hosting, eu-central-1)</li>
          <li>Firebase Cloud Messaging (Google) — push notifications</li>
          <li>Apple Push Notification Service — push iOS</li>
          <li>Anthropic / OpenRouter — AI router (transfer extra-UE
            con Standard Contractual Clauses)</li>
        </ul>

        <h2>Cookie e localStorage</h2>
        <p>
          L&apos;applicazione usa <strong>cookie/localStorage essenziali</strong>{' '}
          per autenticazione (JWT, refresh token), preferenze UI e
          gestione consenso (chiave <code>leopart.cookie.consent</code>).
          Cookie analytics e marketing solo previo consenso esplicito
          dell&apos;utente tramite banner cookie. È possibile
          modificare le preferenze in qualsiasi momento ricaricando il
          banner (cancellare la chiave localStorage).
        </p>

        <h2>Trasferimento extra-UE</h2>
        <p>
          Alcuni sub-processor hanno datacenter al di fuori dello SEE
          (es. Google FCM US, Apple APNS US, Anthropic Claude US per
          AI router). Tali trasferimenti sono regolati dalle{' '}
          <strong>Standard Contractual Clauses (SCC)</strong> art. 46
          GDPR. Il dettaglio dei sub-processor e dei trasferimenti è
          disponibile su richiesta a privacy@leopartmove.com.
        </p>

        <h2>Sicurezza</h2>
        <ul>
          <li>
            Password protette con algoritmo hash bcrypt (cost 10)
          </li>
          <li>
            Comunicazioni in TLS 1.2+ (Let&apos;s Encrypt + AWS
            Certificate Manager)
          </li>
          <li>
            Encryption at-rest sul DB (MongoDB Atlas managed)
          </li>
          <li>
            Audit log completo di ogni operazione (5 anni di
            conservazione, accountability art. 5)
          </li>
          <li>
            Accesso ai dati limitato per ruolo (atleta vede solo
            sé stesso, coach vede solo il proprio team, director
            vede tutti gli atleti del proprio ecosistema)
          </li>
          <li>
            Procedura data breach response art. 33-34 (notifica al
            Garante entro 72h se rischio elevato)
          </li>
        </ul>

        <h2>Atleti minorenni</h2>
        <p>
          LeoPart accetta atleti dai 14 anni in su. Per atleti
          minorenni il trattamento richiede consenso espresso
          esercitato dal/dai genitore/i o tutore/i legale/i (art. 8
          GDPR + D.Lgs. 196/2003 art. 2-quinquies). La società
          sportiva è tenuta a raccogliere e conservare il consenso
          firmato e a metterlo a disposizione del Titolare a
          richiesta.
        </p>

        <h2>Modifiche all&apos;informativa</h2>
        <p>
          La presente Privacy Policy può essere modificata in
          qualsiasi momento. Le modifiche sostanziali (cambio
          finalità, nuovi sub-processor, nuove categorie di dati)
          richiederanno nuovo consenso esplicito tramite popup
          all&apos;accesso successivo all&apos;app. Versione corrente:{' '}
          <code>2026-05-10</code>.
        </p>

        <p className={styles.footer}>
          <em>
            Ultimo aggiornamento: 2026-05-10 · Versione 0.9 (in
            attesa di review legale finale)
          </em>
        </p>
      </div>
    </div>
  );
};

export default PrivacyPage;
